import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getAuth } from '../../contexts/authStore';

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  shiftReminders: boolean;
  shiftChanges: boolean;
  weeklySchedule: boolean;
  submitScheduleNotification: boolean;
  approvalsNotification: boolean;
  passwordChange: boolean;
}

interface NotificationsTabProps {
  notificationSettings: NotificationSettings;
  oldNotificationSettings: NotificationSettings;
  profileData: Record<string, any>;
  userData: Record<string, any>;
  onNotificationChange: (field: string) => void;
  onSave: () => void;
}

export default function NotificationsTab({
  notificationSettings,
  oldNotificationSettings,
  profileData,
  userData,
  onNotificationChange,
  onSave,
}: NotificationsTabProps) {
  const hasChanges = JSON.stringify(notificationSettings) !== JSON.stringify(oldNotificationSettings);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">Notification Preferences</Typography>
        <Button
          disabled={!hasChanges}
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSave}
        >
          Save Changes
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Delivery Methods
          </Typography>
          <FormControlLabel
            control={
              <Switch
                disabled={!profileData.email && !Boolean(getAuth().email_verified)}
                checked={notificationSettings.emailNotifications}
                onChange={() => onNotificationChange('emailNotifications')}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Email Notifications</Typography>
                {!profileData.email && (
                  <Typography variant="caption" color="error">
                    *Please add a verified email in your profile to enable email notifications
                  </Typography>
                )}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                disabled={!profileData.phone && !Boolean(getAuth().phone_number_verified)}
                checked={notificationSettings.smsNotifications}
                onChange={() => onNotificationChange('smsNotifications')}
              />
            }
            label={
              <Box>
                <Typography variant="body2">SMS/Text Notifications</Typography>
                {!profileData.phone && (
                  <Typography variant="caption" color="error">
                    *Please add a verified phone number in your profile to enable SMS notifications
                  </Typography>
                )}
                {notificationSettings.smsNotifications && (
                  <Chip
                    label="Consent Provided"
                    size="small"
                    color="success"
                    icon={<CheckCircleIcon />}
                    sx={{ ml: 1, color: 'background.paper' }}
                  />
                )}
              </Box>
            }
          />
        </Box>

        {/* Shift Notifications - Disabled for now */}
        {/* <Divider />

        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Shift Notifications
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.shiftReminders}
                onChange={() => onNotificationChange('shiftReminders')}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Shift Reminders</Typography>
                <Typography variant="caption" color="text.secondary">
                  Get notified 24 hours before your shift
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.shiftChanges}
                onChange={() => onNotificationChange('shiftChanges')}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Shift Changes</Typography>
                <Typography variant="caption" color="text.secondary">
                  Notify when your shift is updated
                </Typography>
              </Box>
            }
          />
        </Box>*/}
        <Divider />

        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
            Security Notifications
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.passwordChange}
                onChange={() => onNotificationChange('passwordChange')}
                disabled={true}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Password Changes</Typography>
                <Typography variant="caption" color="text.secondary">
                  Get notified when your password is changed
                </Typography>
                <br />
                <Typography variant="caption" color="error">
                  For security reasons this must be turned on and cannot be disabled
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
            Schedule Notifications
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.submitScheduleNotification}
                onChange={() => onNotificationChange('submitScheduleNotification')}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Schedule Submit</Typography>
                <Typography variant="caption" color="text.secondary">
                  Get notified when your next schedule is submitted
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={notificationSettings.approvalsNotification}
                onChange={() => onNotificationChange('approvalsNotification')}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Approvals (Manager/Admin)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Get notified of pending approvals requiring your action
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>
    </Paper>
  );
}
